import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, IconButton, Tooltip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RouteAnalyzer from "./RouteAnalyzer";
import { apiService } from "../../services/api";
import { useTutorialOptional } from "../tutorial/TutorialContext";

const WL_RED = "#EF2D3E";
const FONT = "'Inter', sans-serif";

export interface SavedScenario {
  id: string;
  product: string;
  destination: string;
  quantity: string;
  supplier: string;
  strategy: string;
  savedAt: string;
  routeName?: string;
  from?: string;
  to?: string;
  totalCost?: string;
  riskPct?: number;
  riskColor?: string;
  tariffImpact?: string | null;
  mode?: string;
  modeLabel?: string;
  modeBadgeBg?: string;
  modeBadgeIconColor?: string;
  eta?: string;
}

const PAGE_SIZE = 10;

const formatSavedAt = (savedAt: string): string => {
  const date = new Date(savedAt.endsWith('Z') ? savedAt : savedAt + 'Z');
  return date.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }) + " CST";
};

const RouteOptimizationTabs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"route-options" | "saved-scenarios">("route-options");
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<SavedScenario | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const tutorialCtx = useTutorialOptional();
  const currentStep = tutorialCtx?.isOpen ? tutorialCtx.steps[tutorialCtx.currentStepIndex] : null;
  const isRouteFormStep = currentStep?.id === "route-analyzer";
  const routeExample = isRouteFormStep ? currentStep?.example : undefined;
  const routeTutorialStepId = currentStep?.id;

  useEffect(() => {
    if (currentStep?.id === "route-save") {
      setActiveTab("saved-scenarios");
    } else {
      setActiveTab("route-options");
    }
  }, [currentStep?.id]);

  const loadSavedScenarios = useCallback(async () => {
    try {
      const response = await apiService.getRouteScenarios();
      const scenarios = Array.isArray(response.data) ? response.data : [];
      setSavedScenarios(scenarios);
      setCurrentPage(1);
    } catch (error) {
      console.error("Failed to load route scenarios", error);
      setSavedScenarios([]);
    }
  }, []);

  useEffect(() => {
    loadSavedScenarios().catch(() => undefined);
  }, [loadSavedScenarios]);

  useEffect(() => {
    if (activeTab !== "saved-scenarios") return;
    loadSavedScenarios().catch(() => undefined);
  }, [activeTab, loadSavedScenarios]);

  const scenarioDedupeKey = (s: SavedScenario): string =>
    [s.product, s.destination, s.supplier, s.quantity, s.strategy]
      .map((v) => (v ?? "").trim().toLowerCase())
      .join("|");

  const handleSaveScenario = async (
    scenario: SavedScenario,
  ): Promise<{ overwritten: boolean }> => {
    try {
      const incomingKey = scenarioDedupeKey(scenario);
      const duplicate = savedScenarios.find(
        (item) => item.id !== scenario.id && scenarioDedupeKey(item) === incomingKey,
      );
      if (duplicate) {
        await apiService.deleteRouteScenario(duplicate.id);
      }
      await apiService.saveRouteScenario(scenario);
      setSavedScenarios((prev) => [
        scenario,
        ...prev.filter(
          (item) => item.id !== scenario.id && scenarioDedupeKey(item) !== incomingKey,
        ),
      ]);
      return { overwritten: Boolean(duplicate) };
    } catch (error) {
      console.error("Failed to persist route scenario", error);
      return { overwritten: false };
    }
  };

  const handleDeleteScenario = async (id: string) => {
    try {
      await apiService.deleteRouteScenario(id);
      setSavedScenarios((prev) => prev.filter((scenario) => scenario.id !== id));
    } catch (error) {
      console.error("Failed to delete route scenario", error);
    }
  };

  const handleViewScenario = (scenario: SavedScenario) => {
    setSelectedScenario({ ...scenario });
    setActiveTab("route-options");
  };

  const handleTabChange = (nextTab: "route-options" | "saved-scenarios") => {
    if (nextTab === "route-options" && activeTab !== "route-options") {
      setSelectedScenario(null);
    }
    setActiveTab(nextTab);
  };

  return (
    <div style={{ minHeight: "100%", background: "#F7F7F7", padding: "32px" }}>
      <Box data-tutorial-step="route-header" sx={{ mb: "32px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "1.5rem",
              fontWeight: 600,
              color: "#111111",
              letterSpacing: "-0.3px",
              lineHeight: 1,
            }}
          >
            Compare Routes
          </Typography>
          <Tooltip title="Explore different shipping routes optimized by cost, time, distance, or reliability. Select and save your best options for future reference." placement="right" arrow>
            <span style={{ cursor: "pointer", color: "#6B7280", lineHeight: 1, display: "flex", alignItems: "center" }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </span>
          </Tooltip>
        </Box>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "0.875rem",
            color: "#6B7280",
            mt: "4px",
          }}
        >
          Optimize your supply chain with intelligent route comparison
        </Typography>
      </Box>

      <Box
        data-tutorial-step="route-tabs"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          borderBottom: "1px solid #EAEAEA",
          mb: "24px",
        }}
      >
        {(
          [
            { key: "route-options", label: "Route Options" },
            { key: "saved-scenarios", label: savedScenarios.length ? `Saved Scenarios (${savedScenarios.length})` : "Saved Scenarios" },
          ] as const
        ).map(({ key, label }) => {
          const isActive = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              style={{
                paddingBottom: 12,
                paddingLeft: 4,
                paddingRight: 4,
                fontSize: "0.875rem",
                fontWeight: 500,
                fontFamily: FONT,
                color: isActive ? WL_RED : "#6B7280",
                background: "none",
                border: "none",
                borderBottom: isActive ? `2px solid ${WL_RED}` : "2px solid transparent",
                marginBottom: -1,
                cursor: "pointer",
                transition: "color 0.2s",
              }}
            >
              {label}
            </button>
          );
        })}
      </Box>

      {activeTab === "route-options" && (
        <div data-tutorial-step="route-analyzer">
          <RouteAnalyzer onSaveScenario={handleSaveScenario} selectedScenario={selectedScenario} tutorialExample={routeExample} tutorialStepId={routeTutorialStepId} />
        </div>
      )}

      {activeTab === "saved-scenarios" && (
        <div data-tutorial-step="route-saved">
          {savedScenarios.length === 0 ? (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #EAEAEA",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #EAEAEA" }}>
                <Typography sx={{ fontFamily: FONT, fontSize: "0.875rem", color: "#6B7280" }}>
                  View and manage saved scenario filters from the builder
                </Typography>
              </div>
              <div style={{ textAlign: "center", padding: "80px 24px" }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", margin: "0 auto 12px" }}>
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <Typography sx={{ fontFamily: FONT, color: "#6B7280", fontSize: "0.875rem", mb: 1 }}>
                  No saved scenarios yet
                </Typography>
                <button
                  onClick={() => handleTabChange("route-options")}
                  style={{ background: "none", border: "none", color: WL_RED, fontSize: "0.875rem", fontWeight: 500, fontFamily: FONT, cursor: "pointer" }}
                >
                  Go to Route Options to create one
                </button>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #EAEAEA",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                overflow: "hidden",
              }}
            >
              <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #EAEAEA" }}>
                <Typography sx={{ fontFamily: FONT, fontSize: "0.875rem", color: "#6B7280" }}>
                  Scenarios you explicitly saved from the route results
                </Typography>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#F7F7F7" }}>
                      {["Product", "Destination", "Quantity", "Supplier", "Strategy", "Last Saved", "Actions"].map((header) => (
                        <th
                          key={header}
                          style={{
                            padding: "12px 16px",
                            textAlign: "left",
                            fontSize: 11,
                            fontWeight: 600,
                            fontFamily: FONT,
                            color: "#6B7280",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                            borderBottom: "1px solid #EAEAEA",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {savedScenarios.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((scenario, index, page) => (
                      <tr
                        key={scenario.id}
                        style={{
                          borderBottom: index < page.length - 1 ? "1px solid #EAEAEA" : "none",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#F7F7F7"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 500, color: "#111" }}>
                            {scenario.product || "-"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ fontFamily: FONT, fontSize: 14, color: "#374151" }}>
                            {scenario.destination || scenario.to || "-"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ fontFamily: FONT, fontSize: 14, color: "#374151" }}>
                            {scenario.quantity ? `${scenario.quantity} units` : "-"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ fontFamily: FONT, fontSize: 14, color: "#374151" }}>
                            {scenario.supplier || "Any supplier"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              fontFamily: FONT,
                              fontSize: 11,
                              fontWeight: 500,
                              color: "#1D4ED8",
                              background: "#EFF6FF",
                              padding: "2px 8px",
                              borderRadius: 999,
                              border: "1px solid #BFDBFE",
                              textTransform: "capitalize",
                            }}
                          >
                            {scenario.strategy || "cost"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ fontFamily: FONT, fontSize: 12, color: "#6B7280" }}>
                            {formatSavedAt(scenario.savedAt)}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <Tooltip title="View in Route Options">
                              <IconButton
                                data-tutorial-step={index === 0 ? "route-saved" : undefined}
                                size="small"
                                aria-label="View in Route Options"
                                onClick={() => handleViewScenario(scenario)}
                                sx={{ p: "6px", color: "#6B7280", "&:hover": { color: WL_RED, background: "#FFF1F2" } }}
                              >
                                <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                aria-label="Delete"
                                onClick={() => void handleDeleteScenario(scenario.id)}
                                sx={{ p: "6px", color: "#6B7280", "&:hover": { color: "#dc2626", background: "#FFF1F1" } }}
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {savedScenarios.length > PAGE_SIZE && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderTop: "1px solid #EAEAEA" }}>
                  <span style={{ fontFamily: FONT, fontSize: 13, color: "#6B7280" }}>
                    {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, savedScenarios.length)} of {savedScenarios.length}
                  </span>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: "4px 12px", borderRadius: 6, border: "1px solid #EAEAEA",
                        background: currentPage === 1 ? "#F7F7F7" : "#fff",
                        color: currentPage === 1 ? "#D1D5DB" : "#374151",
                        fontFamily: FONT, fontSize: 13, cursor: currentPage === 1 ? "default" : "pointer",
                      }}
                    >
                      Prev
                    </button>
                    {Array.from({ length: Math.ceil(savedScenarios.length / PAGE_SIZE) }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: "4px 10px", borderRadius: 6,
                          border: page === currentPage ? `1px solid ${WL_RED}` : "1px solid #EAEAEA",
                          background: page === currentPage ? WL_RED : "#fff",
                          color: page === currentPage ? "#fff" : "#374151",
                          fontFamily: FONT, fontSize: 13, cursor: "pointer", fontWeight: page === currentPage ? 600 : 400,
                        }}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(Math.ceil(savedScenarios.length / PAGE_SIZE), p + 1))}
                      disabled={currentPage === Math.ceil(savedScenarios.length / PAGE_SIZE)}
                      style={{
                        padding: "4px 12px", borderRadius: 6, border: "1px solid #EAEAEA",
                        background: currentPage === Math.ceil(savedScenarios.length / PAGE_SIZE) ? "#F7F7F7" : "#fff",
                        color: currentPage === Math.ceil(savedScenarios.length / PAGE_SIZE) ? "#D1D5DB" : "#374151",
                        fontFamily: FONT, fontSize: 13, cursor: currentPage === Math.ceil(savedScenarios.length / PAGE_SIZE) ? "default" : "pointer",
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RouteOptimizationTabs;

